import React, { useState, useMemo } from 'react'
import SearchFilterIcon from '../../assets/icons/search-filter'
// import '../Staffs/style.css'
import CustomTable from '../../components/Table'
import CustomTableHead from '../../components/Table/TableHead'
import CustomTableBody from '../../components/Table/TableBody'
import CustomTableRow from '../../components/Table/TableRow'
import CustomTableData from '../../components/Table/TableData'
import TablePagination from '../../components/Table/Pagination'
import CustomTableDataCTA from '../../components/Table/TableDataCTA'
import profileIcon from '../../assets/icons/profile-icon.svg'
import SearchIcon from '../../assets/icons/search.svg'
import Empty from '../../assets/icons/Empty'
import Modal from '../../components/Modal'
import ServiceRequest from '../../api/service-request-service'
import ArtisanService from '../../api/artisan-services'
import { ServiceRequest as ServiceRequestType } from '../../interfaces/services'
import { showToast } from '../../utils/toast'
import { ServiceItem, ServiceAction } from '../../interfaces/services'
import { getStatusClass, getStatusLabel } from './ModalContent'
import RenderModalContent from './ModalContent'
import RenderModalFooter from './ModalFooter'

const serviceStatuses = [
  { label: 'All', value: 'all' },
  { label: 'Pending', value: 'pending' },
  { label: 'Assigned', value: 'assigned' },
  { label: 'Completed', value: 'completed' },
  { label: 'Cancelled', value: 'cancelled' },
]

const tableHeadData = ['Initiator', 'Service', 'Time', 'Status', 'Description', '']



interface ServicesRequestProps {
  setNumOfServices: (num: number) => void
  handleViewSelect: (view: string, queries?: Record<string, string>) => void
  onBack?: () => void
  serviceId?: string
  estateId?: string
}

const ViewServicesRequest = ({
  setNumOfServices,
  handleViewSelect,
  onBack,
  serviceId,
  estateId,
}: ServicesRequestProps) => {
  const [statusFilter, setStatusFilter] = useState(serviceStatuses[0])
  const [currentPage, setCurrentPage] = useState(1)
  const [showAdd, setShowAdd] = React.useState(false)
  const [showSuccessModal, setShowSuccessModal] = React.useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedService, setSelectedService] = useState<ServiceItem | null>(null)
  const [selectedAction, setSelectedAction] = useState<ServiceAction>(null)

  // Form states for modals
  const [selectedArtisan, setSelectedArtisan] = useState('')
  const [sendViaEmail, setSendViaEmail] = useState(true)
  const [sendViaText, setSendViaText] = useState(false)

  // Loading state for actions
  const [isProcessing, setIsProcessing] = useState(false)

  // Fetch artisans for the selected service
  // const { data: artisansData, isLoading: artisansLoading } = ArtisanService.useGetArtisans({
  //   page: 1,
  //   size: 100,
  //   serviceId: selectedService?.serviceId,
  //   query: undefined,
  //   estateId: undefined,
  // })

  // const artisans = useMemo(() => {
  //   if (!artisansData?.results) return []
  //   return artisansData.results
  // }, [artisansData])

  const LIMIT = 10

  // Fetch service requests from API
  const {
    data: serviceRequestsData,
    error,
    isLoading,
    mutate,
  } = ServiceRequest.useGetServiceRequests({
    page: currentPage,
    limit: LIMIT,
    estateId,
    serviceId,
    status: statusFilter.value !== 'all' ? statusFilter.value : undefined,
    query: searchQuery || undefined,
  })

  // Transform API data to ServiceItem format and keep serviceId for later artisan fetch
  const transformedServices = useMemo(() => {
    if (!serviceRequestsData?.results) return []

    return serviceRequestsData.results.map((item: ServiceRequestType) => {
      const serviceObj: any = item.serviceId ?? {}
      const residentObj: any = item.residentId ?? {}
      const propertyObj: any = item.propertyId ?? {}

      return {
        id: item.id,
        initiatorName: residentObj.fullName ?? 'Unknown',
        initiatorEmail: residentObj.email ?? '',
        initiatorAvatar: profileIcon,
        serviceType: serviceObj.title ?? String(serviceObj),
        serviceId: serviceObj.id ?? serviceObj._id ?? serviceObj.serviceId ?? undefined,
        time: item.createdAt
          ? new Date(item.createdAt).toLocaleString('en-US', {
              weekday: 'short',
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })
          : new Date().toLocaleString('en-US', {
              weekday: 'short',
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            }),
        status: item.status as ServiceItem['status'],
        description: item.description ?? '',
        phoneNumber: residentObj.phone ?? '',
        houseAddress: propertyObj.address ?? '',
        requestCode: item.code ?? '',
        assignedArtisan:
          (item.assignedArtisan && (item.assignedArtisan as any).fullName) || undefined,
      } as ServiceItem
    })
  }, [serviceRequestsData])

  const totalResults = serviceRequestsData?.totalResults || 0
  const totalPages = serviceRequestsData?.totalPages || 1

  React.useEffect(() => {
    setNumOfServices(totalResults)
  }, [totalResults, setNumOfServices])

  const handleStatusSelect = (status: { value: string; label: string }) => {
    setStatusFilter(status)
    setCurrentPage(1)
  }

 
  const handleActionClick = (action: ServiceAction, service: ServiceItem) => {
    setSelectedService(service)
    setSelectedAction(action)
    setIsModalOpen(true)
    setSelectedArtisan(service.assignedArtisan || '')
    // setSendViaEmail(true)
    // setSendViaText(false)
  }

  function handleHeaderBack() {
    if (showSuccessModal) {
      setShowSuccessModal(false)
      return
    }
    if (showAdd) {
      setShowAdd(false)
      return
    }
    onBack && onBack()
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setSelectedService(null)
    setSelectedAction(null)
    setSelectedArtisan('')
  }

  // ---- ARTISANS FETCHING (for selected service) ----
  const { data: artisansResponse, isValidating: artisansLoading } = ArtisanService.useGetArtisans({
    page: 1,
    size: 100,
    serviceId: selectedService?.serviceId,
    search: undefined,
    estateId: undefined,
  })
  
  const artisans = artisansResponse?.results ?? []

  const handleConfirmAction = async () => {
    if (!selectedService || !selectedAction) return

    setIsProcessing(true)

    try {
      switch (selectedAction) {
        case 'cancelRequest':
          await ServiceRequest.cancelRequest(selectedService.id)

          showToast({
            title: 'Success',
            description: 'Service request cancelled successfully.',
            id: 'cancel-success',
            status: 'success',
          })

          // Refresh the data
          mutate()
          closeModal()
          break

        case 'assignArtisan':
        case 'reassign':
          if (!selectedArtisan) {
            showToast({
              title: 'Select artisan',
              description: 'Please choose an artisan before assigning.',
              status: 'error',
              id: 'no-artisan',
            })
            setIsProcessing(false)
            return
          }

          await ServiceRequest.assignArtisan(selectedService.id, selectedArtisan)

          showToast({
            title: 'Success',
            description:
              selectedAction === 'assignArtisan'
                ? 'Artisan assigned successfully.'
                : 'Artisan reassigned successfully.',
            status: 'success',
            id: 'assign-success',
          })

          // Refresh the data
          mutate()
          closeModal()
          break
      }
    } catch (err: any) {
      console.error('Action failed:', err)
      showToast({
        title: 'Error',
        description: err?.message || 'Operation failed. Please try again.',
        status: 'error',
        id: 'action-error',
      })
    } finally {
      setIsProcessing(false)
    }
  }

  const getModalTitle = () => {
    switch (selectedAction) {
      case 'viewDetails':
        return 'View details'
      case 'assignArtisan':
        return 'Assign Artisan'
      case 'reassign':
        return 'Reassign Artisan'
      case 'cancelRequest':
        return 'Cancel request'
      default:
        return ''
    }
  }


  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[400px]">
        <div className="text-gray-600">Loading service requests...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-[400px]">
        <div className="text-red-600">
          Error loading service requests. Please try again.
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="mt-12 grid gap-y-10">
        <header className="flex items-center gap-4 mb-10">
          <button
            type="button"
            aria-label="Back"
            onClick={handleHeaderBack}
            className="p-2 rounded-md hover:bg-gray-100 flex gap-4 items-center"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 18l-6-6 6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="text-lg text-gray-700 font-medium">Services</div>
          </button>
        </header>

        <div className="search-filter-bar flex w-full justify-between gap-2.5">
          <form
            onSubmit={(e) => {
              e.preventDefault()
            }}
            className="w-full flex bg-gray-100 rounded-lg items-center px-2"
          >
            <div className="Relative flex items-center w-full">
              <img src={SearchIcon} alt="Search Icon" />
              <input
                placeholder=" Search by date"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="py-2.5 w-full bg-transparent h-full"
              />
            </div>
          </form>

          <div className="w-fit p-2.5 flex gap-5 whitespace-nowrap relative group w-44 justify-end font-semibold">
            <p>{statusFilter.label}</p>
            <div className="bg-gray-100 w-8 h-8 flex items-center justify-center rounded-lg">
              <SearchFilterIcon />
            </div>
            <div className="absolute z-10 top-12 right-0 invisible group-hover:visible cursor-pointer w-40 rounded-xl overflow-hidden shadow-[4px_4px_20px_4px_rgba(33,37,41,0.10)] border bg-white">
              {serviceStatuses.map((status) => (
                <p
                  onClick={() => handleStatusSelect(status)}
                  className={`hover:bg-gray-200 p-3 ${status.value === statusFilter.value ? 'text-[#069E2D]' : ''} border-t`}
                  key={status.value}
                >
                  {status.label}
                </p>
              ))}
            </div>
          </div>
        </div>

        <CustomTable>
          <CustomTableHead dataList={tableHeadData} />
          {transformedServices.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={tableHeadData.length}>
                  <div className="flex flex-col items-center justify-center h-[300px] w-full gap-4">
                    <Empty />
                    <p className="text-sm font-semibold">No results found</p>
                  </div>
                </td>
              </tr>
            </tbody>
          ) : (
            <CustomTableBody>
              {transformedServices.map((item) => (
                <CustomTableRow key={item.id}>
                  <CustomTableData
                    dataLabel="Initiator"
                  >
                    <div className="flex items-center">
                       <img src={item.initiatorAvatar} alt="initiator" className="w-8 h-8 rounded-full mr-2" />
                      <div>
                      <div className="font-medium">{item.initiatorName}</div>
                      <div className="text-xs text-gray-500">{item.initiatorEmail}</div>
                      </div>
                     
                    </div>
                  </CustomTableData>
                  <CustomTableData dataLabel="Service" text={item.serviceType} />
                  <CustomTableData dataLabel="Time" text={item.time} />
                  <CustomTableData
                    dataLabel="Status"
                    text={getStatusLabel(item.status)}
                    className={getStatusClass(item.status)}
                  />
                  <CustomTableData dataLabel="Description" text={item.description} />
                  <CustomTableDataCTA dataLabel="More" text="...">
                    <p
                      onClick={() => handleActionClick('viewDetails', item)}
                      className="hover:bg-gray-200 p-3 border-t"
                    >
                      View details
                    </p>
                    {(item.status === 'completed' || item.status === 'pending') && (
                      <p
                        onClick={() => handleActionClick('cancelRequest', item)}
                        className="hover:bg-gray-200 p-3 border-t text-red-500"
                      >
                        Cancel request
                      </p>
                    )}
                    {item.status === 'assigned' && (
                      <p
                        onClick={() => handleActionClick('reassign', item)}
                        className="hover:bg-gray-200 p-3 border-t"
                      >
                        Reassign
                      </p>
                    )}
                    {item.status === 'pending' && (
                      <p
                        onClick={() => handleActionClick('assignArtisan', item)}
                        className="hover:bg-gray-200 p-3 border-t"
                      >
                        Assign Artisan
                      </p>
                    )}
                  </CustomTableDataCTA>
                </CustomTableRow>
              ))}
            </CustomTableBody>
          )}
        </CustomTable>

        {transformedServices.length > 0 && (
          <TablePagination
            totalPages={totalPages}
            currPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        )}

        <Modal
          open={isModalOpen}
          close={closeModal}
          title={getModalTitle()}
          showHeader
          showClose
          footer={
            <RenderModalFooter
              selectedAction={selectedAction}
              closeModal={closeModal}
              handleConfirmAction={handleConfirmAction}
              isProcessing={isProcessing}
              selectedArtisan={selectedArtisan}
            />
          }
          widthClass="w-[calc(100%-32px)] sm:max-w-md md:max-w-[640px]"
        >
          <RenderModalContent
            selectedService={selectedService}
            selectedAction={selectedAction}
            selectedArtisan={selectedArtisan}
            setSelectedArtisan={setSelectedArtisan}
            sendViaEmail={sendViaEmail}
            setSendViaEmail={setSendViaEmail}
            sendViaText={sendViaText}
            setSendViaText={setSendViaText}
            onStatusUpdated={mutate}
          />
        </Modal>
      </div>
    </>
  )
}

export default ViewServicesRequest
